import type React from 'react';
import { useCallback, useState, ReactNode, useEffect } from 'react';
import { Button, Modal as PolarisModal } from '@shopify/polaris';
import { Modal as ShopifyModal, TitleBar, useAppBridge } from '@shopify/app-bridge-react';

// 按钮配置接口
export interface ButtonConfig {
  content: string;
  onAction: () => void;
  primary?: boolean;
  destructive?: boolean;
  disabled?: boolean;
}

// 定义组件接收的属性
export interface ShopifyModalProps {
  // 基本属性
  size?: 'small' | 'base' | 'large' | 'max'; // 可选的 variant 属性
  title: string; // 标题属性
  id: string; // ID 属性
  open: boolean; // 控制模态框是否打开
  onClose: () => void; // 关闭模态框的回调函数
  
  // 内容
  children: ReactNode; // 模态框的内容
  
  // 操作按钮
  primaryButton?: ButtonConfig; // 主要操作按钮
  secondaryButtons?: ButtonConfig[]; // 次要操作按钮
  
  // 仅在非 Shopify 环境中使用的属性
  showTriggerButton?: boolean; // 是否显示触发按钮
  triggerButtonText?: string; // 触发按钮文字
}

const ModalComponent: React.FC<ShopifyModalProps> = ({ 
  size = 'base',
  title,
  id = 'my-modal',
  open,
  onClose,
  children,
  primaryButton,
  secondaryButtons = [],
  showTriggerButton = false,
  triggerButtonText = '打开模态框'
}) => {
  // 检查是否在 Shopify 环境中
  const isShopifyEnv = Boolean(window.shopify);
  // 在非 Shopify 环境中使用内部状态管理模态框
  const [polarisModalActive, setPolarisModalActive] = useState(open);
  // 使用 useAppBridge hook (仅在 Shopify 环境中有效)
  const shopify = isShopifyEnv ? useAppBridge() : null;
  
  // 当 open 属性变化时更新内部状态
  useEffect(() => {
    if (isShopifyEnv && shopify?.modal) {
      if (open) {
        shopify.modal.show(id);
      } else {
        shopify.modal.hide(id);
      }
    } else {
      setPolarisModalActive(open);
    }
  }, [open, isShopifyEnv, id, shopify]);
  
  // 打开模态框
  const handleOpenModal = useCallback(() => {
    if (isShopifyEnv && shopify?.modal) {
      shopify.modal.show(id);
    } else {
      setPolarisModalActive(true);
    }
    // 不调用 onClose 因为这是打开操作
  }, [id, shopify, isShopifyEnv]);

  // 关闭模态框
  const handleCloseModal = useCallback(() => {
    if (isShopifyEnv && shopify?.modal) {
      shopify.modal.hide(id);
    } else {
      setPolarisModalActive(false);
    }
    onClose();
  }, [id, shopify, isShopifyEnv, onClose]);

  // 生成 Shopify TitleBar 的按钮
  const renderShopifyButtons = () => {
    const buttons = [];
    
    // 添加次要按钮
    if (secondaryButtons && secondaryButtons.length > 0) {
      secondaryButtons.forEach((btn, index) => {
        buttons.push(
          <button
            key={`secondary-${index}`}
            onClick={btn.onAction}
            disabled={btn.disabled}
            tone={btn.destructive ? 'critical' : undefined}
          >
            {btn.content}
          </button>
        );
      });
    }
    
    // 添加主要按钮
    if (primaryButton) {
      buttons.push(
        <button 
          key="primary"
          variant="primary" 
          onClick={primaryButton.onAction}
          disabled={primaryButton.disabled}
          tone={primaryButton.destructive ? 'critical' : undefined}
        >
          {primaryButton.content}
        </button>
      );
    }
    
    return buttons;
  };

  // 为 Polaris Modal 生成 primaryAction
  const polarisMainAction = primaryButton ? {
    content: primaryButton.content,
    onAction: primaryButton.onAction,
    disabled: primaryButton.disabled,
    destructive: primaryButton.destructive
  } : undefined;
  
  // 为 Polaris Modal 生成 secondaryActions
  const polarisSecondaryActions = secondaryButtons.length > 0 
    ? secondaryButtons.map(btn => {
        return {
          content: btn.content,
          onAction: btn.onAction,
          disabled: btn.disabled,
          destructive: btn.destructive
        };
      })
    : undefined;

  return (
    <>
      {/* 显示触发按钮（可选） */}
      {showTriggerButton && (
        <Button onClick={handleOpenModal}>{triggerButtonText}</Button>
      )}
      
      {isShopifyEnv ? (
        // Shopify 环境中使用 App Bridge Modal
        <ShopifyModal id={id} variant={size} onHide={handleCloseModal}>
          <div style={{ padding: '20px' }}>
            {children}
          </div>
          
          {/* 模态框标题栏 */}
          <TitleBar title={title}>
            {renderShopifyButtons()}
          </TitleBar>
        </ShopifyModal>
      ) : (
        // 非 Shopify 环境中使用 Polaris Modal
        <PolarisModal
          open={polarisModalActive}
          onClose={handleCloseModal}
          title={title}
          primaryAction={polarisMainAction}
          secondaryActions={polarisSecondaryActions}
        >
          <PolarisModal.Section>
            {children}
          </PolarisModal.Section>
        </PolarisModal>
      )}
    </>
  );
};

export default ModalComponent;